import { useCallback, useEffect, useRef, useState } from 'react';

import {
  INTERSECTION_OBSERVER_THRESHOLD,
  MAX_AMOUNT_VISIBLE_OPTIONS,
} from '../ProgramAccordion.constants';

const useAccordionsObserver = optionsAmount => {
  const accordionRefsMap = useRef(new Map());
  const heightRefsMap = useRef(new Map());
  const [visibilityMap, setVisibilityMap] = useState({});
  const observersRef = useRef({ intersection: null, resize: null });

  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      entries => {
        const updates = entries.reduce((acc, entry) => {
          const { id } = entry.target.dataset;

          if (id) {
            acc[id] = entry.isIntersecting;
          }

          return acc;
        }, {});

        if (Object.keys(updates).length) {
          setVisibilityMap(prev => ({ ...prev, ...updates }));
        }
      },
      {
        threshold: INTERSECTION_OBSERVER_THRESHOLD,
      },
    );

    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const { id } = entry.target.dataset;

        if (id) {
          heightRefsMap.current.set(id, entry.contentRect.height);
        }
      });
    });

    observersRef.current = { intersection: intersectionObserver, resize: resizeObserver };

    return () => {
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  const registerAccordion = useCallback((id, element) => {
    if (!element) {
      accordionRefsMap.current.delete(id);

      return;
    }

    accordionRefsMap.current.set(id, element);

    if (observersRef.current.intersection && observersRef.current.resize) {
      observersRef.current.intersection.observe(element);
      observersRef.current.resize.observe(element);
    }
  }, []);

  const getAccordionObserverProps = useCallback(
    id => {
      const isAccordionVisible = visibilityMap[id] || false;
      const height = heightRefsMap.current.get(id) || 0;
      const isShownSummary = isAccordionVisible || optionsAmount < MAX_AMOUNT_VISIBLE_OPTIONS;

      return {
        isAccordionVisible,
        height,
        isShownSummary,
      };
    },
    [visibilityMap, optionsAmount],
  );

  return { registerAccordion, getAccordionObserverProps };
};

export default useAccordionsObserver;
